import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { AppContext, AppContextType } from '../AppContext'
import { AudioSourceInfo, AudioTrackWithInfo, UserContext, UserContextType } from '../UserContext'
import {
    MuseWSMessage,
    WSJoinRoomIntentData,
    WSJoinRoomResponseBody,
    WSJoinRoomResponseBodyPeers,
    WSPeerDisconnectPreambleData,
    WSRoomMetadataUpdatedData,
    WSUserRemovedData,
} from '../pages/CollaboratePage/Types'
import {
    ApiRoomDocument,
    AudioEngineRemoteAudio,
    isSessionStateEqual,
    MidiStats,
    P2pLocalAudioManifest,
    RoomContext,
    RoomContextState,
    RoomContextType,
    roomWaitlistHasAppSerialId,
    SfuLocalAudioManifest,
    toSessionState,
} from '../RoomContext'
import { arrayDiff } from '../utils'
import { persistent } from './LocalStorage'
import { logger } from './Logger'
import MuseP2pPeerManager from './MuseP2pPeerManager'
import useRoomContextRef from './useRoomContextRef'
import useUserContextRef from './useUserContextRef'
import { wsConnection } from './WSConnectionInstance'
import useSfuRoomService from './useSfuRoomService'
import useRoomActions, { joinRoomState } from './useRoomActions'
import moment from 'moment'
import usePreviousV2 from '../hooks/usePreviousV2'
import { MusePeer } from './RoomContextMusePeer'
import useRootSnackbar from './useRootSnackbar'
import useManifestMethods from './useManifestMethods'
import useRemoteDesktopControlClient from './useRemoteDesktopControlClient'
import { MIDIInput, MIDIOutput } from '../types/WebMidi'
import ElectronIPC from './ElectronIPC'
import { DataPacket_Kind, LocalVideoTrack, Participant, Room, RoomEvent } from 'livekit-client'
import { useScreenRecordingService } from '../functionalities/ScreenRecording/useScreenRecordingService'
import useArrayDiff from '../hooks/useArrayDiff'
import { WSConnectionSendError } from './WSConnectionManager'
import useMidiPassthroughService from './useMidiPassthorughService'
import { PeerMidiActivityEventEmitter } from './PeerMidiActivityEventEmitter'
import { decodeExtendedDeviceId, encodeExtendedDeviceId, isDefinedDeviceId } from '../Logic'
import useRemoteDesktopControlPermissionActions from './useRemoteDesktopControlPermissionActions'
import useAppNotificationActions from '../functionalities/AppNotification/useAppNotificationActions'
import useAudioEngineRoomService from './useAudioEngineRoomService'
import {
    AudioEngineError,
    AudioEngineNetworkStats,
    getAudioEngineInstance,
    IAudioEngineRoom,
} from '../core/AudioEngine'
import usePeerPingMeasurement from './usePeerPingMeasurement'
import { RtcStatsUtils } from '../functionalities/SystemStats/RtcStatsUtils'
import { serializeError } from 'serialize-error'
import FakeMediaStreamTrack from '../core/AudioEngine/FakeMediaStreamTrack'
import { AudioEngineStreamRemoteStatsSummary } from '../core/AudioEngine/RemoteStatsSummaryBuilder'
import useAppContextRef from './useAppContextRef'
import { AnalyticsEvents } from './AnalyticsService'
import useAnalytics from './useAnalytics'
import { env } from './EnvironmentVariables'
import { winKeyHook } from './IpcWinKeyHook'
import usePeerMediaDeviceContextSyncMonitorService from './usePeerMediaDeviceContextSyncMonitorService'
import LiveKitManifestSupport from './LiveKitManifestSupport'
import _ from 'lodash'
import useLocalManifestTransmitter from './useLocalManifestTransmitter'
import * as Constants from '../services/Constants'
import { speakerDetectionManager } from './SpeakerDetectionManager'
import { sessionViewModeState } from '@/pages/SessionPage/internal/shared/useViewMode'
import Api from './Api'

const log = logger.createChildLogger({ name: 'RoomController' })

const fftSize = 32

export default function RoomController() {
    const { state, dispatch } = useContext(UserContext) as UserContextType
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state: roomState, dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType
    const { logUserActivityWithContext } = useAnalytics()

    useSfuRoomService()

    useAudioEngineRoomService()

    useMidiPassthroughService()

    const { show: showSnackbar } = useRootSnackbar()

    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const currSelectedMidiOutputDevice = useRef<MIDIOutput | null>(null)

    useScreenRecordingService()

    usePeerMediaDeviceContextSyncMonitorService()

    const { onReceivedPingFromPeer, onReceivedPongFromPeer, getLatestHostPingMeasurement } = usePeerPingMeasurement()

    const { generateP2pManifest, generateLegacySfuManifest, generateSfuManifest } = useManifestMethods()

    const {
        joinRoom,
        leaveRoom,
        switchRoom,
        handleWaitlistUser,
        sendMessageToPeer,
        broadcastMessage,
        onStartRemoteRecordingHandler,
        onStopRemoteRecordingHandler,
        onRemoteRecordingErrorHandler,
        onRemoteRecordingChunkHandler,
        onRecordingTriggered,
        stopRemoteAudioRecord,
        endCurrRemoteAudioRecord,
        sendRoomUserControlMessageToServer,
        getCachedRemoteAudioVolume,
        foreachRemoteAudioDevices,
        onRecentSpeakerListUpdate,
        myAudioDeviceMuteAll,
    } = useRoomActions()

    useLocalManifestTransmitter()

    const { rdpHandler } = useRemoteDesktopControlClient()

    const { revoke: remoteDesktopControlRevoke } = useRemoteDesktopControlPermissionActions()

    const { showAppNotification } = useAppNotificationActions()

    const midiStatsRef = useRef<MidiStats>({ txCount: 0, rxCount: 0 })

    const joinRoomPeersHandler = useCallback(
        (data: WSJoinRoomResponseBodyPeers) => {
            // `iceServers` ignored since v1.5.10. The `Constants.data.ice_servers` is
            // directly used.
            const { peers, networkTopology /*, iceServers*/ } = data

            // Check if the session is newly created or if a user is joining an existing one
            const isNewSession = roomStateRef.current.isHost && peers.length === 0
            if (isNewSession) {
                logUserActivityWithContext(AnalyticsEvents.SESSION_CREATED)
            } else if (roomStateRef.current.isHost) {
                logUserActivityWithContext(AnalyticsEvents.HOST_REJOINED)
            }
            log.checkpoint(`join_room_success`, ``, {
                peers: peers.map((a) => ({
                    appSerialId: a.appSerialId,
                    appVersion: a.appVersion,
                    sessionId: a.sessionId,
                    username: a.username,
                    userUrn: a.userUrn,
                })),
            })

            // allocate a peerConnection for each client.
            let peersNext = { ...roomStateRef.current.peers }
            let peerUserProfileCacheNext = { ...roomStateRef.current.peerUserProfileCache }

            for (let peer of peers) {
                if (peersNext[peer.sessionId] == null) {
                    peersNext[peer.sessionId] = MusePeer({
                        userUrn: peer.userUrn,
                        username: peer.username,
                        displayName: peer.displayName,
                        isGuest: peer.isGuest ?? false,
                        sessionId: peer.sessionId,
                        appSerialId: peer.appSerialId,
                        appVersion: peer.appVersion,
                        clientType: peer.clientType,
                        callInitiator: 'me',
                    })
                }
                peerUserProfileCacheNext[peer.userUrn] = {
                    userUrn: peer.userUrn,
                    username: peer.username,
                    displayName: peer.displayName,
                    isGuest: peer.isGuest,
                }
            }

            // we take into account the case where join-room gets called while reconnecting in
            // while in sfu, after the ws server restarted; in which case roomState.expectedNetworkTopology === 'sfu'
            // but the provided networkTopology is 'p2p'. we should _not_ overwrite the 'sfu' into 'p2p'.
            let expectedNetworkTopologyNext: 'p2p' | 'sfu' = roomStateRef.current.expectedNetworkTopology || 'p2p'
            if (networkTopology === 'sfu') {
                expectedNetworkTopologyNext = 'sfu'
            }

            if (expectedNetworkTopologyNext === 'sfu') {
                for (let sessionId of Object.keys(peersNext)) {
                    const peer = peersNext[sessionId]
                    // check with LiveKit if the peer is connected to the sfu room;
                    // then, mark `connectionStatus` as 'connected'.
                    const sfuRoom = roomStateRef.current.sfuRoom
                    if (sfuRoom) {
                        const p = sfuRoom.getParticipantByIdentity(peer.sessionId)
                        if (p) {
                            peer.connectionStatus = 'connected'
                        }
                    }
                }
            }

            log.checkpoint(`join_room_success_dispatch_peers`, ``, {
                peersNext: Object.keys(peersNext).map((k) => ({
                    appSerialId: peersNext[k].appSerialId,
                    sessionId: peersNext[k].sessionId,
                    appVersion: peersNext[k].appVersion,
                    username: peersNext[k].username,
                    displayName: peersNext[k].displayName,
                    isGuest: peersNext[k].isGuest,
                    userUrn: peersNext[k].userUrn,
                    callInitiator: peersNext[k].callInitiator,
                })),
                expectedNetworkTopologyNext,
            })

            roomDispatch({
                type: 'update_room_context',
                value: {
                    // liveKitToken: liveKitToken,
                    expectedNetworkTopology: expectedNetworkTopologyNext,

                    peers: peersNext,

                    // Store the peer info, keyed by the urn. This will be later used to resolve the
                    // peerUrn into usernames and other peer profile data.
                    peerUserProfileCache: peerUserProfileCacheNext,
                    myWaitlistStatus:
                        roomStateRef.current.myWaitlistStatus !== 'isAccepted'
                            ? 'isAccepted'
                            : roomStateRef.current.myWaitlistStatus,

                    subrooms: data.room?.subrooms || [],
                    sessionId: data.museSessionId,
                },
            })

            log.verbose('speakerDetection', 'resetActiveState() on join room')
            speakerDetectionManager.resetActiveState()
        },
        [roomDispatch, roomStateRef]
    )

    const receiveDmPeerCallHandler = useCallback(
        (msg: MuseWSMessage) => {
            const {
                senderSessionId,
                recipientSessionId,
                roomUrn,
                senderUserUrn,
                senderUsername,
                senderDisplayName,
                senderIsGuest,
                senderAppSerialId,
                senderAppVersion,
                clientType,
            } = msg.data

            log.checkpoint(`p2p_recv_dm_peer_call`, ``, {
                senderUserUrn,
                senderUsername,
                senderAppSerialId,
                senderAppVersion,
            })

            let peersNext = { ...roomStateRef.current.peers }

            peersNext[senderSessionId] = MusePeer({
                userUrn: senderUserUrn,
                username: senderUsername,
                displayName: senderDisplayName,
                isGuest: senderIsGuest,
                sessionId: senderSessionId,
                appSerialId: senderAppSerialId,
                appVersion: senderAppVersion,
                clientType: clientType,
                callInitiator: 'them',
            })

            let peerUserProfileCacheNext = { ...roomStateRef.current.peerUserProfileCache }
            peerUserProfileCacheNext[senderUserUrn] = {
                userUrn: senderUserUrn,
                username: senderUsername,
                displayName: senderDisplayName,
                isGuest: senderIsGuest,
            }

            // trigger MuseP2pPeerManager to make new SimplePeer() and similar for sfu
            roomDispatch({
                type: 'update_room_context',
                value: {
                    peers: peersNext,
                    peerUserProfileCache: peerUserProfileCacheNext,
                },
            })

            // TODO: this timeout is ugly. We're waiting for the respective sfu/p2p controllers
            // to ready the p2pConnection/sfuRoom in roomState.
            setTimeout(() => {
                wsConnection.sendMessage(
                    {
                        type: 'peer-reply',
                        data: {
                            senderSessionId: recipientSessionId,
                            recipientSessionId: senderSessionId,
                            roomUrn: roomUrn,
                            ready: true,
                        },
                        uuid: msg.uuid,
                    },
                    {
                        isReliable: true,
                        checkAck: true,
                    }
                )
            }, 500)
        },
        [roomDispatch, roomStateRef]
    )

    const receiveDmPeerPingHandler = useCallback((msg: MuseWSMessage) => {
        const { senderSessionId, recipientSessionId, roomUrn, senderUserUrn, senderUsername, senderAppSerialId } =
            msg.data

        wsConnection.sendMessage(
            {
                type: 'peer-reply',
                data: {
                    senderSessionId: recipientSessionId,
                    recipientSessionId: senderSessionId,
                    roomUrn: roomUrn,
                    ready: true,
                },
                uuid: msg.uuid,
            },
            {
                isReliable: true,
                checkAck: true,
            }
        )
    }, [])

    const joinRoomHandler = useCallback(async (data: WSJoinRoomResponseBody) => {
        log.verbose(`joinRoomHandler`, ``, { data })
        clearTimeout(joinRoomState.joinTimeoutTimer)

        if ('error' in data) {
            const code = data.error.code
            // @ts-ignore -- hack: for 'room_full', the 'code' field is
            // used in place of the message for older versions of the app.
            const code2 = data.error.code2

            log.checkpoint(`join_room_failed:${code}`, `cause="${data.error.cause}"`)
            if (code === 'denied_by_host') {
                const msg = 'The host denied you entry to the room.'
                leaveRoom({ endSession: false, showDialogWithMessage: msg })
            } else if (code === 'room_not_found') {
                const msg = 'Room not found.'
                leaveRoom({ endSession: false, showDialogWithMessage: msg })
            } else if (code === 'session_id_invalidated') {
                const cause = data.error.cause
                if (cause === 'denied_by_host' || cause === 'removed_by_host') {
                    const msg = 'The host removed you from the room.'
                    leaveRoom({ endSession: false, showSnackbarWithMessage: msg })
                } else if (cause === 'timeout') {
                    // TODO: try rejoining the room since this sessionId got invalidated by being timed out.
                    const msg = `You timed out while trying to rejoin the room. Please try again.`
                    leaveRoom({ endSession: false, showDialogWithMessage: msg })
                } else {
                    const msg = `An unexpected error occurred: "${cause}". Please try joining the room again.`
                    leaveRoom({ endSession: false, showDialogWithMessage: msg })
                }
            } else if (code === 'room_full' || code2 === 'room_full') {
                if (Constants.proPolicies.includes(roomStateRef.current.roomTierPolicy?.id)) {
                    const msg = `The room is full, but the host has been notified. Please try again later.`
                    leaveRoom({ endSession: false, showDialogWithMessage: msg })
                } else {
                    appDispatch({
                        type: 'update_app_state',
                        value: {
                            showUpgradeToMuseProDialog: {
                                type: userStateRef.current.isGuest ? 'sessionIsFull:guest' : 'sessionIsFull:caller',
                            },
                        },
                    })
                    roomDispatch({
                        type: 'update_room_context',
                        value: { myWaitlistStatus: 'isFullSession' },
                    })
                    leaveRoom({ endSession: false, stayInCollaboratePage: true })
                }
            } else if (code2 === 'host_max_active_sessions') {
                appDispatch({
                    type: 'update_app_state',
                    value: {
                        showTooManyHostedSessionsDialog: {
                            isHost: roomStateRef.current.isHost,
                        },
                    },
                })
                leaveRoom({ endSession: false, stayInCollaboratePage: false, suppressSessionFeedback: true })
            } else {
                const msg = `An unexpected error occurred: "${code}". ` + `Please restart the app and rejoin the room.`
                leaveRoom({ endSession: false, showDialogWithMessage: msg })
            }

            return
        }

        if ('isRedirect' in data && data.isRedirect) {
            try {
                const password = roomStateRef.current.parentRoom?.password || undefined
                const response = await Api.getRoom(data.redirectRoomUrn, password)
                if (response) {
                    if (response.parentRoom) {
                        // is a subroom
                        showSnackbar({
                            severity: 'success',
                            message: `Moved to your assigned studio` + (response.name ? `: ${response.name}` : ''),
                        })
                    } else {
                        showSnackbar({
                            severity: 'success',
                            message: `Moved back to the Main Room`,
                        })
                    }
                    switchRoom(data.redirectRoomUrn)
                } else {
                    showSnackbar({
                        severity: 'error',
                        message: 'Could not join room: failed to get data.',
                    })
                }
            } catch (e) {
                showSnackbar({
                    severity: 'error',
                    message: `Could not join room: "${(e as unknown as any)?.message}".`,
                })
            }
            return
        }

        if ('isWaitlisted' in data) {
            log.checkpoint(`join_room_waitlisted`)
            roomDispatch({
                type: 'update_room_context',
                value: {
                    myWaitlistStatus: 'isWaitlisted',
                },
            })
        } else if ('peers' in data) {
            joinRoomPeersHandler(data)
        }

        // JOIN_ROOM_LINK is consumed.
        env.JOIN_ROOM_LINK = ''
    }, [])

    const showNotificationDebounced = useMemo(() => _.debounce(showAppNotification, 1000), [])

    const peerJoinRoomAcceptRequestHandlerHelper = useCallback(
        (dataArray: WSJoinRoomIntentData[]) => {
            let roomWaitlistNext = [...roomStateRef.current.roomWaitlist]
            let hasChanges = false

            for (const data of dataArray) {
                const existingItem = roomWaitlistNext.find((a) => a.appSerialId === data.appSerialId) || null
                if (existingItem) {
                    if (existingItem.isBeingJoinedIn) {
                        // then call 'allow' one more time.
                        log.checkpoint(`recv_peer_join_room_accept_request:handle`, ``, {
                            ...data,
                            debugMessage: 'call handleWaitlistUser()',
                        })
                        handleWaitlistUser(data, true)
                    }
                    continue // Skip to next item
                }

                // add new roomWaitlist item into the list.
                const added = {
                    profile: {
                        userUrn: data.userUrn,
                        username: data.username,
                        displayName: data.displayName,
                    },
                    isGuest: data.isGuest,
                    sessionId: data.sessionId,
                    appSerialId: data.appSerialId,
                    isBeingJoinedIn: false,
                }
                roomWaitlistNext.push(added)
                hasChanges = true

                log.verbose(`peerJoinRoomAcceptRequestHandler`, `is not a known peer; adding to roomWaitlist.`, {
                    ...data,
                })

                showNotificationDebounced({
                    title: `${added.profile.displayName || added.profile.username || ''} wants to join the room`,
                    body: `You can allow or deny the user to join the room on the app's side panel.`,
                })
            }

            // Only dispatch once if there were any changes
            if (hasChanges && !_.isEqual(roomStateRef.current.roomWaitlist, roomWaitlistNext)) {
                log.checkpoint(`recv_peer_join_room_accept_request:handle`, ``, {
                    debugMessage: 'roomWaitlist changed; call roomDispatch to update roomWaitlist.',
                })
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        roomWaitlist: roomWaitlistNext,
                        isRoomWaitlistDialogOpen: roomWaitlistNext.length > 0,
                    },
                })
            }
        },
        [handleWaitlistUser, roomDispatch, roomStateRef, showNotificationDebounced]
    )

    const peerJoinRequestThrottleListRef = useRef<WSJoinRoomIntentData[]>([])
    const peerJoinRoomAcceptRequestHandlerDebounced = useMemo(() => {
        return _.throttle(() => {
            peerJoinRoomAcceptRequestHandlerHelper(peerJoinRequestThrottleListRef.current)
            peerJoinRequestThrottleListRef.current.length = 0 // Clear the array
        }, 1000)
    }, [peerJoinRoomAcceptRequestHandlerHelper])

    const peerJoinRoomAcceptRequestHandler = useCallback(
        (data: WSJoinRoomIntentData) => {
            const isSelf = data.sessionId === userStateRef.current.sessionId
            if (isSelf) {
                return
            }

            if (!roomStateRef.current.isHost) {
                return
            }

            const isDuplicate = peerJoinRequestThrottleListRef.current.some((existingData) =>
                _.isEqual(existingData, data)
            )
            if (!isDuplicate) {
                peerJoinRequestThrottleListRef.current.push(data)
            }
            peerJoinRoomAcceptRequestHandlerDebounced()
        },
        [handleWaitlistUser, roomDispatch, roomStateRef, sendRoomUserControlMessageToServer, userStateRef]
    )

    const checkPeerExistsOnServer = useCallback(
        async (sessionId: string) => {
            try {
                // ask wsConnection if the peer still exists.
                const response = await wsConnection.sendMessage(
                    {
                        type: 'get-room-metadata',
                        data: {
                            sessionId: userStateRef.current.sessionId,
                            roomUrn: roomStateRef.current.roomUrn,
                        },
                    },
                    {
                        isReliable: true,
                        checkAck: true,
                    }
                )

                if (response instanceof WSConnectionSendError) {
                    return null
                } else if (response != null) {
                    const matchingPeer = (response?.clients || []).find((a: any) => a.sessionId === sessionId)
                    if (matchingPeer) {
                        // pass
                        return true
                    } else {
                        return false
                    }
                } else {
                    return null
                }
            } catch (e) {
                return null
            }
        },
        [roomStateRef, userStateRef]
    )

    const endConnectionWithPeer = useCallback(
        (sessionId: string, reason: 'removed_by_host') => {
            const peerToDisconnect = roomStateRef.current.peers[sessionId]
            if (!peerToDisconnect) {
                return
            }

            if (reason === 'removed_by_host') {
                showSnackbar({
                    severity: 'info',
                    message: `The host removed ${peerToDisconnect.username} from the room.`,
                })
            }

            roomDispatch({
                type: 'update_peer',
                value: {
                    sessionId: peerToDisconnect.sessionId,
                    peer: {
                        ...peerToDisconnect,
                        connectionStatus: 'closing',
                        lastDisconnectPreambleTime: new Date(),
                        lastErrorCode: null,
                    },
                },
            })
        },
        [roomDispatch, roomStateRef, showSnackbar]
    )

    const roomMetadataUpdatedHandler = useCallback(
        (data: Partial<WSRoomMetadataUpdatedData>) => {
            log.verbose(`roomMetadataUpdatedHandler`, `called. data=`, data)

            if (data.networkTopology != null) {
                if (data.networkTopology !== roomStateRef.current.expectedNetworkTopology) {
                    log.verbose(
                        `roomMetadataUpdatedHandler`,
                        `current roomState.expectedNetworkTopology="${roomStateRef.current.expectedNetworkTopology}", ` +
                            `whereas the networkTopology given by the server is "${data.networkTopology}". Updating room.`
                    )
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            expectedNetworkTopology: data.networkTopology,
                        },
                    })

                    const hasRemotePeers = Object.keys(roomStateRef.current.peers).length > 0
                    if (roomStateRef.current.expectedNetworkTopology === 'p2p' && hasRemotePeers) {
                        showSnackbar({
                            severity: 'info',
                            message: 'Please wait a moment while we upgrade your connection.',
                        })
                    }
                }
            }
        },
        [roomDispatch, roomStateRef, showSnackbar]
    )

    const disconnectPreambleHandler = useCallback(
        (data: WSPeerDisconnectPreambleData) => {
            log.checkpoint('recv_disconnect_preamble', '', data)
            roomDispatch({
                type: 'update_peer',
                value: {
                    sessionId: data.fromSessionId,
                    peer: {
                        lastDisconnectPreambleTime: new Date(),
                    },
                },
            })

            const p2pConn = roomStateRef.current.peers[data.fromSessionId]?.p2pConnection
            if (p2pConn) {
                try {
                    p2pConn.end()
                } catch (e) {}
            }

            if (data.endSession) {
                // TODO: show message
                leaveRoom({ endSession: true })
            }
        },
        [leaveRoom, roomDispatch]
    )

    const userRemovedHandler = useCallback((data: WSUserRemovedData) => {
        const { sessionId, isYou, code } = data
        // @ts-ignore -- hack: for 'session_time_ran_out', the 'code' field is
        // used in place of the message for older versions of the app.
        const code2 = data.code2

        if (isYou) {
            let msg = ''
            if (code === 'host_closed_room') {
                // The host closed the room.
                msg = 'The host closed the room'
            } else if (code === 'removed_by_host') {
                // The host removed you from the room.
                msg = 'The host removed you from the room.'
            } else if (code === 'session_time_ran_out' || code2 === 'session_time_ran_out') {
                msg = 'The session has ended'
            } else if (code === 'duplicate_user_in_room') {
                msg = 'Another user with the same username joined the room.'
            } else {
                msg = `You were removed from the room: "${code}".`
            }

            leaveRoom({ endSession: false, showSnackbarWithMessage: msg })
        } else {
            endConnectionWithPeer(sessionId, 'removed_by_host')
        }
    }, [])

    useEffect(
        function roomControllerWsMessageRouter() {
            log.verbose(`roomControllerWsMessageRouter`, `load`)

            function onopen() {
                log.verbose(`roomControllerWsMessageRouter:onopen`, `entered.`)
                if (roomStateRef.current.roomUrn) {
                    log.verbose(
                        `roomControllerWsMessageRouter:onopen`,
                        `calling join-room on ` + `roomUrn="${roomStateRef.current.roomUrn}".`
                    )
                    log.checkpoint(`call_join_room_on_ws_reconnect`)
                    joinRoom(roomStateRef.current.roomUrn, { createNewSessionId: false })
                } else {
                    log.verbose(`roomControllerWsMessageRouter:onopen`, `roomUrn is null so ` + `no-op.`)
                }
            }

            function onmessage(msg: MuseWSMessage) {
                if (!roomStateRef.current.roomUrn) {
                    return
                }

                if (msg.type === 'dm-peer') {
                    // receiving dm-peer from a remote peer ()
                    const subtype = msg.data?.type
                    if (subtype === 'call') {
                        receiveDmPeerCallHandler(msg)
                    } else if (subtype === 'ping') {
                        receiveDmPeerPingHandler(msg)
                    }
                } else if (msg.type === 'join-room') {
                    joinRoomHandler(msg.data as WSJoinRoomResponseBody)
                } else if (msg.type === 'join-room-accept-request') {
                    peerJoinRoomAcceptRequestHandler(msg.data as WSJoinRoomIntentData)
                } else if (msg.type === 'join-room-accept-request-full-room') {
                    if (Constants.proPolicies.includes(roomStateRef.current.roomTierPolicy.id)) {
                        const username = msg.data?.isGuest ? `${msg.data?.displayName} (guest)` : msg.data?.username
                        appDispatch({
                            type: 'new_message_dialog',
                            value: {
                                id: 'room_full_error_dialog',
                                text: `A user, ${username}, failed to join the session because it's at full capacity. Please contact support if you need to host larger sessions.`,
                            },
                        })
                    } else {
                        const username = msg.data?.displayName || msg.data?.username || ''
                        appDispatch({
                            type: 'update_app_state',
                            value: { showUpgradeToMuseProDialog: { type: 'sessionIsFull:host', username } },
                        })
                    }
                } else if (msg.type === 'room-metadata-updated') {
                    roomMetadataUpdatedHandler(msg.data as Partial<WSRoomMetadataUpdatedData>)
                } else if (msg.type === 'disconnect-preamble') {
                    disconnectPreambleHandler(msg.data as WSPeerDisconnectPreambleData)
                } else if (msg.type === 'user-removed') {
                    userRemovedHandler(msg.data as WSUserRemovedData)
                } else if (msg.type === 'ws_authorization_error') {
                    const msg = 'Authorization failed.'
                    leaveRoom({ endSession: false, showDialogWithMessage: msg })
                } else if (msg.type === 'tier-update') {
                    appDispatch({
                        type: 'update_app_state',
                        value: { showAccountIsUpgradedDialog: true },
                    })
                } else if (msg.type === 'session-state-updated') {
                    const a = roomStateRef.current.sessionState
                    const b = toSessionState(msg.data.sessionState)
                    if (!isSessionStateEqual(a, b)) {
                        roomDispatch({
                            type: 'update_room_context',
                            value: { sessionState: b },
                        })
                    }

                    if (msg.data.roomTierPolicy) {
                        roomDispatch({
                            type: 'update_room_context',
                            value: { roomTierPolicy: msg.data.roomTierPolicy },
                        })
                    }

                    if (!a.isBoosted && b.isBoosted) {
                        if (appStateRef.current.appSettings.doNotShowRoomBoostedDialog) {
                            showSnackbar({
                                severity: 'info',
                                message: 'Boost applied!',
                            })
                        } else {
                            appDispatch({
                                type: 'update_app_state',
                                value: { showSessionIsBoostedDialog: true },
                            })
                        }

                        if (roomStateRef.current.myWaitlistStatus === 'isFullSession') {
                            joinRoom(roomStateRef.current.roomUrn)
                            appDispatch({
                                type: 'update_app_state',
                                value: { showUpgradeToMuseProDialog: null },
                            })
                        }
                    }
                } else if (msg.type === 'recent-speaker-list-update') {
                    const recentSpeakerList: string[] = msg.data?.recentSpeakerList || []
                    onRecentSpeakerListUpdate(recentSpeakerList)
                }
            }

            function onerror() {}

            wsConnection.addEventListener('onopen', onopen)
            wsConnection.addEventListener('onmessage', onmessage)
            wsConnection.addEventListener('onerror', onerror)

            return () => {
                wsConnection.removeEventListener('onopen', onopen)
                wsConnection.removeEventListener('onmessage', onmessage)
                wsConnection.removeEventListener('onerror', onerror)
            }
        },
        [
            disconnectPreambleHandler,
            joinRoom,
            joinRoomHandler,
            peerJoinRoomAcceptRequestHandler,
            receiveDmPeerCallHandler,
            roomMetadataUpdatedHandler,
            roomStateRef,
            userRemovedHandler,
        ]
    )

    const connectionStatuses = Object.keys(roomState.peers).map(
        (sessionId) => roomState.peers[sessionId].connectionStatus
    )
    const prevConnectionStatuses = useRef<string[]>([])

    useEffect(
        function remoteDisconnectedPeerIfDifferentSamePeerJoins() {
            // toString() works b/c primitive elements.
            if (connectionStatuses.toString() === prevConnectionStatuses.current.toString()) {
                return
            }

            log.verbose(`remoteDisconnectedPeerIfDifferentSamePeerJoins`, `called.`, {
                connectionStatuses,
                prevConnectionStatuses: prevConnectionStatuses.current,
            })

            prevConnectionStatuses.current = connectionStatuses

            const peerSessionIds = Object.keys(roomStateRef.current.peers)
            const disconnectingStatuses = [
                'disconnected_standby',
                'disconnected_should_initiate_reconnect',
                'closing',
                'closed',
            ]
            // remove other peers that have the same userUrn and is not connected.
            const sessionIdsToRemove = new Set<string>()
            peerSessionIds.forEach((sessionId) => {
                const thisPeerObj = roomStateRef.current.peers[sessionId]
                const thisAppSerialId = thisPeerObj?.appSerialId
                peerSessionIds.forEach((otherSessionId) => {
                    const otherPeerObj = roomStateRef.current.peers[otherSessionId]
                    const otherAppSerialId = otherPeerObj?.appSerialId
                    if (thisPeerObj !== otherPeerObj && thisAppSerialId === otherAppSerialId) {
                        const thisConnStatus = roomStateRef.current.peers[sessionId].connectionStatus
                        const otherConnStatus = roomStateRef.current.peers[otherSessionId].connectionStatus

                        log.verbose(
                            `remoteDisconnectedPeerIfDifferentSamePeerJoins`,
                            `in roomState.peers list, detected ` + `two peers that are "the same":`,
                            {
                                a: {
                                    sessionId: sessionId,
                                    appSerialId: thisAppSerialId,
                                    connectionStatus: thisConnStatus,
                                },
                                b: {
                                    sessionId: otherSessionId,
                                    appSerialId: otherAppSerialId,
                                    connectionStatus: otherConnStatus,
                                },
                            }
                        )
                        if (
                            !disconnectingStatuses.includes(thisConnStatus) &&
                            disconnectingStatuses.includes(otherConnStatus)
                        ) {
                            log.verbose(
                                `remoteDisconnectedPeerIfDifferentSamePeerJoins`,
                                `marking sessionId="${otherSessionId}" ` +
                                    `to remove from roomState.peers list because it is disconnecting/disconnected and an equivalent peer ` +
                                    `(as identified by appSerialId="${otherAppSerialId}") already exists in the peers list.`
                            )
                            sessionIdsToRemove.add(otherSessionId)
                        }
                    }
                })
            })

            if (sessionIdsToRemove.size > 0) {
                for (let sessionIdToRemove of sessionIdsToRemove) {
                    const peersNext = { ...roomStateRef.current.peers }
                    delete peersNext[sessionIdToRemove]
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            peers: peersNext,
                        },
                    })
                }
            }
        },
        [connectionStatuses, roomDispatch, roomStateRef]
    )

    const peersLeftRoomNotificationAcc = useRef<string[]>([])
    const peersLeftRoomNotificationTimeout = useRef<NodeJS.Timeout | undefined>(undefined)
    const peersLeftRoomNotificationHandler = useCallback((name: string) => {
        peersLeftRoomNotificationAcc.current.push(name)
        console.log('peersLeftRoomNotificationAcc.current=' + JSON.stringify(peersLeftRoomNotificationAcc.current))

        peersLeftRoomNotificationTimeout.current = setTimeout(() => {
            const names = peersLeftRoomNotificationAcc.current
            if (names.length > 5) {
                const namesStr = names.slice(0, 5).join(', ')
                showAppNotification({
                    title: `${namesStr} and ${names.length - 5} others left the room.`,
                    body: ' ',
                    muteAudioIfPossible: true,
                })
            } else if (names.length > 0) {
                showAppNotification({
                    title: `${names.join(', ')} left the room.`,
                    body: ' ',
                    muteAudioIfPossible: true,
                })
            }
            peersLeftRoomNotificationAcc.current = []
        }, 500)
    }, [])

    const peerLeftRoomUnexpectedHandler = useCallback(
        (peer: MusePeer) => {
            if (roomStateRef.current.expectedNetworkTopology === 'p2p') {
                const connectionStatusNext =
                    peer.callInitiator === 'me' ? 'disconnected_should_initiate_reconnect' : 'disconnected_standby'

                log.verbose(
                    `peerClosingReactionJob`,
                    `setting "${peer.sessionId}"'s connectionStatus to ` +
                        `"${connectionStatusNext}" because the peer disconnected unexpectedly ` +
                        `(peer_connection_close_unexpected) under networkTopology='p2p'.`
                )

                roomDispatch({
                    type: 'update_peer',
                    value: {
                        sessionId: peer.sessionId,
                        peer: {
                            connectionStatus: connectionStatusNext,
                            remoteSfuAudios: [],
                            remoteSfuDesktop: null,
                            remoteSfuVideo: null,
                        },
                    },
                })
            } else {
                log.verbose(
                    `peerClosingReactionJob`,
                    `removing "${peer.sessionId}" from the peers list ` +
                        `from getting peer_connection_close_unexpected under networkTopology='sfu'.`
                )

                const peersNext = { ...roomStateRef.current.peers }
                delete peersNext[peer.sessionId]
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        peers: peersNext,
                    },
                })

                if (userStateRef.current.clientType !== 'listenonly' && peer.manifest?.clientType !== 'listenonly') {
                    peersLeftRoomNotificationHandler(peer.displayName || peer.username)
                }
            }
        },
        [roomDispatch, roomStateRef]
    )

    const peerLeftRoomExpectedHandler = useCallback(
        (peer: MusePeer) => {
            log.verbose(
                `peerClosingReactionJob`,
                `removing "${peer.sessionId}" from roomState.peers list ` +
                    `since the user exited normally (peer_connection_close_expected).`
            )

            const peersNext = { ...roomStateRef.current.peers }
            delete peersNext[peer.sessionId]

            roomDispatch({
                type: 'update_room_context',
                value: {
                    peers: peersNext,
                },
            })
            if (userStateRef.current.clientType !== 'listenonly' && peer.manifest?.clientType !== 'listenonly') {
                peersLeftRoomNotificationHandler(peer.displayName || peer.username)
            }
        },
        [roomDispatch, roomStateRef]
    )

    const determinePeerClosingExpectedOrUnexpected = useCallback(
        (lastDisconnectPreambleTime: Date | null, code: string | null, peer: MusePeer) => {
            let dt: number | null
            if (lastDisconnectPreambleTime) {
                dt = moment().diff(lastDisconnectPreambleTime, 'seconds')
            } else {
                dt = null
            }
            if (dt != null && dt <= 30) {
                log.checkpoint('peer_connection_close_expected', '', {
                    peerSessionId: peer.sessionId,
                    peerUserUrn: peer.userUrn,
                    peerUsername: peer.username,
                    code: code,
                    lastDisconnectPreambleTime: lastDisconnectPreambleTime,
                    dt: dt,
                })
                return 'expected'
            } else {
                log.checkpoint('peer_connection_close_unexpected', '', {
                    peerSessionId: peer.sessionId,
                    peerUserUrn: peer.userUrn,
                    peerUsername: peer.username,
                    code: code,
                    lastDisconnectPreambleTime: lastDisconnectPreambleTime,
                    dt: dt,
                })
                return 'unexpected'
            }
        },
        []
    )

    const peerClosingReactionJob = useCallback(
        (peer: MusePeer, room: RoomContextState) => {
            const lastDisconnectPreambleTime = peer.lastDisconnectPreambleTime
            const code = peer.lastErrorCode

            const disconnectType = determinePeerClosingExpectedOrUnexpected(lastDisconnectPreambleTime, code, peer)

            if (disconnectType === 'unexpected') {
                peerLeftRoomUnexpectedHandler(peer)
            } else {
                peerLeftRoomExpectedHandler(peer)
            }
        },
        [determinePeerClosingExpectedOrUnexpected, peerLeftRoomExpectedHandler, peerLeftRoomUnexpectedHandler]
    )

    const prevConnectionStatuses2 = useRef<string[]>([])
    useEffect(
        function peerConnectionStatusChangedReaction() {
            // toString() works b/c primitive elements.
            if (connectionStatuses.toString() === prevConnectionStatuses2.current.toString()) {
                return
            }
            prevConnectionStatuses2.current = connectionStatuses

            log.verbose(`peerConnectionStatusChangedReaction`, `called.`)

            let roomWaitlistNext = [...roomStateRef.current.roomWaitlist]

            Object.keys(roomStateRef.current.peers).forEach((sessionId) => {
                const peer = roomStateRef.current.peers[sessionId]
                if (peer.connectionStatus === 'closing') {
                    log.verbose(
                        `peerConnectionStatusChangedReaction`,
                        `calling peerClosingReactionJob for peer ` +
                            `sessionId="${peer.sessionId}" username="${peer.username}".`
                    )
                    peerClosingReactionJob(peer, roomStateRef.current)
                }

                if (peer.connectionStatus === 'connected') {
                    log.verbose(
                        `peerConnectionStatusChangedReaction`,
                        `detected connectionStatus "connected" for ` +
                            `sessionId="${peer.sessionId}" username="${peer.username}".`
                    )
                    if (roomWaitlistHasAppSerialId(roomStateRef.current.roomWaitlist, peer.appSerialId)) {
                        log.verbose(
                            `peerConnectionStatusChangedReaction`,
                            `roomWaitlist has the sessionId so removing the user ` + `from the roomWaitlist.`
                        )
                        roomWaitlistNext = roomWaitlistNext.filter((item) => item.appSerialId !== peer.appSerialId)
                    }
                }
            })

            if (!_.isEqual(roomStateRef.current.roomWaitlist, roomWaitlistNext)) {
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        roomWaitlist: roomWaitlistNext,
                    },
                })
            }
        },
        [connectionStatuses, peerClosingReactionJob, roomDispatch, roomState.peers, roomStateRef]
    )

    const { current: prevPeers } = usePreviousV2(roomState.peers)
    const lastUserJoinedNotificationTimeRef = useRef(new Date(0))
    useEffect(() => {
        if (prevPeers == null) {
            return
        }

        Object.keys(roomState.peers).forEach((sessionId) => {
            const peer = roomState.peers[sessionId]
            const now = roomState.peers[peer.sessionId]
            if (now == null) {
                return
            }

            const prev = prevPeers[peer.sessionId] || {}

            // 1) When I am sharing screen and screen control request comes in from remote peer
            // Older clients send with no `receiverSessionId` field, but the request is
            // directed only to us. For newer clients, message may not be directed to us, indicated
            // by the `receiverSessionId` field.
            const rq = now.manifest.screenControlRequest
            const isReceiverAddressedToMe =
                rq.receiverSessionId == null || rq.receiverSessionId === userStateRef.current.sessionId

            log.isVerbose() &&
                log.verbose('checkScreenControlRequest', '', {
                    peerSessionId: peer.sessionId,
                    peerUsername: peer.username,
                    userSessionId: userStateRef.current.sessionId,
                    isReceiverAddressedToMe,
                    nowManifestScreenControlRequest: now.manifest.screenControlRequest,
                    prevManifestScreenControlRequest: prev.manifest?.screenControlRequest,
                })

            if (
                now.manifest.screenControlRequest?.reqId > 0 &&
                !_.isEqual(now.manifest.screenControlRequest, prev.manifest?.screenControlRequest) &&
                isReceiverAddressedToMe
            ) {
                if (now.manifest.screenControlRequest.op === 'start') {
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            isRemoteDesktopControlRequestPromptOpen: {
                                reqId: now.manifest.screenControlRequest.reqId,
                                fromSessionId: peer.sessionId,
                                fromUsername: peer.username,
                            },
                        },
                    })
                } else if (now.manifest.screenControlRequest.op === 'stop') {
                    remoteDesktopControlRevoke()
                }
            }

            // 2) When a remote peer is sharing screen and sends revoked/denied/accepted response back.
            const rr = now.manifest.lastScreenControlRequestResponse
            const isRequesterAddressedToMe =
                !rr.requesterSessionId || rr.requesterSessionId === userStateRef.current.sessionId
            if (
                now.manifest.lastScreenControlRequestResponse?.reqId > 0 &&
                JSON.stringify(now.manifest.lastScreenControlRequestResponse) !==
                    JSON.stringify(prev.manifest?.lastScreenControlRequestResponse)
            ) {
                if (
                    now.manifest.lastScreenControlRequestResponse.reqId ===
                        roomStateRef.current.screenControlRequest?.reqId &&
                    isRequesterAddressedToMe
                ) {
                    const status = now.manifest.lastScreenControlRequestResponse.status
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            remoteDesktopControlStatus: {
                                reqId: now.manifest.lastScreenControlRequestResponse.reqId,
                                status: status,
                                sessionId: now.sessionId,
                                username: now.username,
                            },
                        },
                    })
                }

                // revoke myself if someone else is allowed
                if (
                    !isRequesterAddressedToMe &&
                    now.manifest.lastScreenControlRequestResponse.status === 'allowed' &&
                    roomStateRef.current.remoteDesktopControlStatus?.status === 'allowed'
                ) {
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            remoteDesktopControlStatus: {
                                ...roomStateRef.current.remoteDesktopControlStatus,
                                status: 'revoked',
                            },
                        },
                    })
                }
            }

            // follow host's galleryClientOrdering if setting is enabled by the host.
            if (
                peer.userUrn === roomStateRef.current.host?.userUrn &&
                peer.manifest.applyHostGalleryPrefsToAllParticipants
            ) {
                if (Array.isArray(peer.manifest?.galleryClientOrdering)) {
                    const { galleryClientOrdering } = peer.manifest
                    if (!_.isEqual(galleryClientOrdering, sessionViewModeState.mode.videoOrder)) {
                        sessionViewModeState.setVideoOrder(galleryClientOrdering)
                    }
                }

                if (Array.isArray(peer.manifest?.videoHiddenSessionIds)) {
                    const { videoHiddenSessionIds } = peer.manifest
                    if (!_.isEqual(videoHiddenSessionIds, roomStateRef.current.videoHiddenSessionIds)) {
                        roomDispatch({
                            type: 'update_room_context',
                            value: { videoHiddenSessionIds },
                        })
                    }
                }
            }
        })

        // clearing remoteDesktopControlStatus if sessionId does not exist anymore.
        if (roomStateRef.current.remoteDesktopControlStatus?.sessionId) {
            if (!roomState.peers[roomStateRef.current.remoteDesktopControlStatus.sessionId]) {
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        remoteDesktopControlStatus: null,
                    },
                })
            }
        }

        // clearing remoteDesktopBeingControlledStatus if sessionId does not exist anymore.
        if (appStateRef.current.appState.remoteDesktopBeingControlledStatus?.sessionId) {
            if (!roomState.peers[appStateRef.current.appState.remoteDesktopBeingControlledStatus.sessionId]) {
                appDispatch({
                    type: 'update_app_state',
                    value: {
                        remoteDesktopBeingControlledStatus: null,
                    },
                })
            }
        }

        if (new Date().getTime() - lastUserJoinedNotificationTimeRef.current.getTime() > 5_000) {
            // ring chime if `allowUserAutoJoin` is set b/c otherwise, the remote
            // peers enter silently without any indication.
            if (roomStateRef.current.allowUserAutoJoin) {
                const peersDiff = arrayDiff(Object.keys(roomState.peers), Object.keys(prevPeers))
                const peersDiffAddedNormalOnly = peersDiff.added.filter((sessionId) => {
                    const peerClientType = roomState.peers[sessionId]?.clientType || ''
                    if (peerClientType === 'listenonly') {
                        return false // skip
                    } else {
                        return true // add to list
                    }
                })
                if (peersDiffAddedNormalOnly.length > 0 && userStateRef.current.clientType !== 'listenonly') {
                    showAppNotification({ title: '', body: '', audioOnly: true })
                    lastUserJoinedNotificationTimeRef.current = new Date()
                }
            }
        }

        // TODO: clearing remoteRecordingStatus if sessionId or track does not exist anymore.
    }, [prevPeers, roomDispatch, roomState.peers, roomStateRef])

    useEffect(
        function remoteRecordingStatusDisconnectHandler() {
            // on them-recording-me, these fields are involved in `roomState.remoteRecordingStatus`:
            // {
            //     isBeingRecorded: boolean
            //     recordInitiatorPeer?: { userUrn: string, username: string, sessionId: string } | null
            //     recordingTrack?: AudioInputDescriptor | null
            // }
            // we must check that `recordInitiatorPeer` exists in `roomState.peers` and
            // that in `state.mediaSelection.audioInputs`, `recordingTrack` exists.
            //
            // on me-recording-them, these fields are involved:
            // {
            //     isRecording?: boolean
            //     peerSessionId?: string // set if we're the recording initiator: the peer that we're recording.
            //     sourceTrackId?: string // set if we're the recording initiator: the peer's track that we're recording.
            // }
            // we must check that `peerSessionId` exists in `roomState.peers` and that
            // in the peer's `peer.remoteAudios`, `sourceTrackId` exists.

            // them-recording-me
            if (
                roomState.remoteRecordingStatus?.isBeingRecorded &&
                roomState.remoteRecordingStatus?.recordInitiatorPeer
            ) {
                const peerSessionId = roomState.remoteRecordingStatus.recordInitiatorPeer.sessionId
                // check peer exists
                const has = Object.keys(roomState.peers).includes(peerSessionId)
                if (!has) {
                    // then user removed.
                    onStopRemoteRecordingHandler()
                } else {
                    // check track exists
                    const exists = state.mediaSelection.audioInputs.find(
                        (a) => a.deviceId === roomState.remoteRecordingStatus.recordingTrack?.deviceId
                    )
                    if (!exists) {
                        // then track removed.
                        onStopRemoteRecordingHandler()
                    }
                }
            }

            // me-recording-them
            if (
                roomState.remoteRecordingStatus?.isRecording === true &&
                roomState.remoteRecordingStatus?.peerSessionId
            ) {
                const peerSessionId = roomState.remoteRecordingStatus.peerSessionId
                const sourceTrackId = roomState.remoteRecordingStatus.sourceTrackId || ''
                // check peer exists
                const has = Object.keys(roomState.peers).includes(peerSessionId)
                if (!has) {
                    // then user removed.
                    // using `onRecordingTriggered` is a bit of a break in abstraction but we can't use
                    // `stopRemoteAudioRecord()` because the peer object no longer exists.
                    onRecordingTriggered(peerSessionId, sourceTrackId, 'stop')
                } else {
                    // check track exists
                    const remoteAudios = roomState.peers[peerSessionId]?.remoteAudios || []
                    const exists = remoteAudios.find((a) => a.info.deviceId === sourceTrackId)
                    if (!exists) {
                        // using `onRecordingTriggered` is a bit of a break in abstraction but we include
                        // this to immediately set `remoteRecordingStatus.isRecording` to false instead of 'stopping'.
                        onRecordingTriggered(peerSessionId, sourceTrackId, 'stop')
                        stopRemoteAudioRecord(roomState.peers[peerSessionId], sourceTrackId)
                    }
                }
            }
        },
        [roomState.remoteRecordingStatus, roomState.peers, state.mediaSelection.audioInputs]
    )

    useEffect(
        function remoteAudioSoloPolicy() {
            let soloedIds = new Set()
            foreachRemoteAudioDevices(roomState.peers, (peer, _, deviceId) => {
                const states = peer.remoteAudioContolStates[deviceId] || {}
                if (states.isSoloed) {
                    soloedIds.add(peer.sessionId + deviceId)
                }
            })

            const d: { [key: string]: Partial<MusePeer> } = {}
            if (soloedIds.size === 0) {
                // no devices soloed; set all `mutedBySolo` to false.
                foreachRemoteAudioDevices(roomState.peers, (peer, _, deviceId) => {
                    const states = peer.remoteAudioContolStates[deviceId] || {}
                    if (states.isMutedBySolo) {
                        // NOTE: setting `d[sessionId].remoteAudioContolStates[deviceId].isMutedBySolo` with vivification.
                        d[peer.sessionId] = {
                            remoteAudioContolStates: {
                                ...(peer.remoteAudioContolStates || {}),
                                [deviceId]: {
                                    ...(peer.remoteAudioContolStates[deviceId] || {}),
                                    isMutedBySolo: false,
                                },
                            },
                        }
                    }
                })
            } else {
                // at least one device soloed; set all `muteBySolo` to true unless
                // the device is soloed.
                foreachRemoteAudioDevices(roomState.peers, (peer, _, deviceId) => {
                    const states = peer.remoteAudioContolStates[deviceId] || {}
                    if (!!states.isMutedBySolo === !!states.isSoloed) {
                        // NOTE: setting `d[sessionId].remoteAudioContolStates[deviceId].isMutedBySolo` with vivification.
                        d[peer.sessionId] = {
                            remoteAudioContolStates: {
                                ...(peer.remoteAudioContolStates || {}),
                                [deviceId]: {
                                    ...(peer.remoteAudioContolStates[deviceId] || {}),
                                    isMutedBySolo: !!!states.isSoloed,
                                },
                            },
                        }
                    }
                })
            }

            if (Object.keys(d).length > 0) {
                // need to update peer.remoteAudioControlStates for all peers/deviceIds in delta.
                roomDispatch({
                    type: 'update_peer',
                    value: Object.keys(d).map((sessionId) => ({ sessionId: sessionId, peer: d[sessionId] })),
                })
            }
        },
        [roomState.peers]
    )

    const setEnabledDebounceTimerRef = useRef<Record<string, NodeJS.Timeout>>({})
    const localProAudioStreamsTriggering = useRef<Map<string, boolean>>(new Map())
    const syncLocalAudioTrackMuteState = (track: AudioTrackWithInfo, chgrp: number[]) => {
        const shouldBeMuted = !!roomState.myAudioMuteState[encodeExtendedDeviceId(track.info.deviceId, chgrp)]
        const autoMuteEnabled = !!roomState.autoMuteEnabled[encodeExtendedDeviceId(track.info.deviceId, chgrp)]
        let shouldBeAutoMuted = false
        if (autoMuteEnabled) {
            for (const [deviceId, triggering] of localProAudioStreamsTriggering.current.entries()) {
                if (triggering && deviceId !== track.info.deviceId) {
                    shouldBeAutoMuted = true
                    break
                }
            }
        }

        const isEnabled = (() => {
            if (track instanceof FakeMediaStreamTrack) {
                return track.getEnabledOfChannelGroup(chgrp)
            } else {
                return track.enabled
            }
        })()

        const setEnabled = (track: AudioTrackWithInfo, b: boolean) => {
            if (track instanceof FakeMediaStreamTrack) {
                track.setEnabledOfChannelGroup(b, chgrp)
            } else {
                track.enabled = b

                const k = encodeExtendedDeviceId(track.info.deviceId, chgrp)

                clearTimeout(setEnabledDebounceTimerRef.current[k] || undefined)
                setEnabledDebounceTimerRef.current[k] = setTimeout(() => {
                    track.enabled = b
                }, 100)
            }
        }

        if ((shouldBeMuted || shouldBeAutoMuted) && isEnabled) {
            setEnabled(track, false)
        } else if (!shouldBeMuted && !shouldBeAutoMuted && !isEnabled) {
            setEnabled(track, true)
        }
    }

    const lastTrigger = useRef<Map<string, number>>(new Map())
    const lastNoTrigger = useRef<Map<string, number>>(new Map())
    const audioAnalyzers = useRef<Map<string, AnalyserNode>>(new Map())
    const audioContext = useRef<AudioContext | null>(null)
    const frequencyBins = useRef<Float32Array>(new Float32Array(fftSize / 2))

    function processAutoMute() {
        const now = Date.now()

        let didChange = false
        let currentDevices: string[] = []
        userStateRef.current.mediaStreams.audioInputs.forEach((track) => {
            let triggering = false
            const deviceId = track.info.deviceId
            currentDevices.push(deviceId)
            if (!(track instanceof FakeMediaStreamTrack)) {
                if (!audioAnalyzers.current.has(deviceId)) {
                    if (!audioContext.current) {
                        audioContext.current = new AudioContext()
                    }
                    const analyzer = audioContext.current.createAnalyser()
                    analyzer.fftSize = fftSize
                    const mediaStreamSource = audioContext.current?.createMediaStreamSource(new MediaStream([track]))
                    mediaStreamSource.connect(analyzer)
                    audioAnalyzers.current.set(deviceId, analyzer)
                }

                const analyzer = audioAnalyzers.current.get(deviceId)
                if (!analyzer) {
                    return
                }
                analyzer.getFloatTimeDomainData(frequencyBins.current)

                let sumSquares = 0.0
                for (const amplitude of frequencyBins.current) {
                    sumSquares += amplitude * amplitude
                }
                const volume = Math.sqrt(sumSquares / frequencyBins.current.length) * 100

                triggering = volume > appStateRef.current.appSettings.autoMuteThreshold
            } else {
                for (const v of track.currOutputVolumePerChannel) {
                    if (v > appStateRef.current.appSettings.autoMuteThreshold) {
                        triggering = true
                        break
                    }
                }
            }

            if (triggering) {
                lastTrigger.current.set(deviceId, now)
            } else {
                lastNoTrigger.current.set(deviceId, now)
            }

            if (triggering !== localProAudioStreamsTriggering.current.get(deviceId)) {
                if (
                    !triggering &&
                    now - (lastTrigger.current.get(deviceId) ?? 0) > appStateRef.current.appSettings.autoMuteReleaseMs
                ) {
                    localProAudioStreamsTriggering.current.set(deviceId, false)
                    didChange = true
                    log.verbose('automute', `device "${deviceId}" is no longer triggering.`)
                } else if (
                    triggering &&
                    now - (lastNoTrigger.current.get(deviceId) ?? 0) > appState.appSettings.autoMuteAttackMs
                ) {
                    localProAudioStreamsTriggering.current.set(deviceId, true)
                    didChange = true
                    log.verbose('automute', `device "${deviceId}" is now triggering.`)
                }
            }
        })

        for (const [deviceId, triggering] of localProAudioStreamsTriggering.current.entries()) {
            if (!currentDevices.includes(deviceId)) {
                if (triggering) {
                    didChange = true
                    log.verbose('automute', `device "${deviceId}" is missing, removing automute state.`)
                }
                localProAudioStreamsTriggering.current.delete(deviceId)
            }
        }

        if (didChange) {
            let autoMuteTriggeringStreams = Object.fromEntries(localProAudioStreamsTriggering.current)
            roomDispatch({
                type: 'update_room_context',
                value: { autoMuteTriggeringStreams },
            })
            log.verbose('automute', `Currently triggering devices`, { autoMuteTriggeringStreams })

            userStateRef.current.mediaStreams.audioInputs.forEach((track) => {
                if (!track) {
                    return
                }

                track.info.channelSelection.forEach((chgrp) => {
                    syncLocalAudioTrackMuteState(track, chgrp)
                })
            })
        }
    }

    useEffect(
        function muteDevicesFromControlStates() {
            // local audio
            state.mediaStreams.audioInputs.forEach((track) => {
                if (!track) {
                    return
                }

                track.info.channelSelection.forEach((chgrp) => {
                    syncLocalAudioTrackMuteState(track, chgrp)
                })
            })

            // local video
            if (state.mediaStreams.video instanceof LocalVideoTrack) {
                const track = state.mediaStreams.video.mediaStreamTrack
                if (track) {
                    if (roomState.myVideoMuteState === true && track.enabled) {
                        track.enabled = false
                    } else if (!roomState.myVideoMuteState && !track.enabled) {
                        track.enabled = true
                    }
                }
            }

            // remote audio
            foreachRemoteAudioDevices(roomState.peers, (peer, media, extDeviceId) => {
                const states = peer.remoteAudioContolStates[extDeviceId] || {}
                const shouldBeMuted = states.isMuted || states.isMutedBySolo

                // chgrp may be undefined if not audio engine.
                const { chgrp } = decodeExtendedDeviceId(extDeviceId) as any

                const isCurrentlyEnabled = (() => {
                    if (media.fromTopology === 'ae') {
                        return media.track.getEnabledOfChannelGroup(chgrp)
                    } else {
                        return media.track.enabled
                    }
                })()

                if (shouldBeMuted && isCurrentlyEnabled) {
                    const next = false
                    if (media.fromTopology === 'ae') {
                        media.track.setEnabledOfChannelGroup(next, chgrp)
                    } else {
                        media.track.enabled = next
                    }
                } else if (!shouldBeMuted && !isCurrentlyEnabled) {
                    const next = true
                    if (media.fromTopology === 'ae') {
                        media.track.setEnabledOfChannelGroup(next, chgrp)
                    } else {
                        media.track.enabled = next
                    }
                }
            })

            // remote video
            Object.keys(roomState.peers).forEach((sessionId) => {
                const peer = roomState.peers[sessionId]
                if (peer.remoteVideo) {
                    const shouldBeMuted = !!peer.remoteVideoControlStates?.isMuted
                    if (shouldBeMuted && peer.remoteVideo.track.enabled) {
                        peer.remoteVideo.track.enabled = false
                    } else if (!shouldBeMuted && !peer.remoteVideo.track.enabled) {
                        peer.remoteVideo.track.enabled = true
                    }
                }
            })

            let autoMuteEnabled = false
            outerloop: for (const track of state.mediaStreams.audioInputs) {
                for (const chgrp of track.info.channelSelection) {
                    const deviceId = encodeExtendedDeviceId(track.info.deviceId, chgrp)
                    if (roomState.autoMuteEnabled[deviceId]) {
                        autoMuteEnabled = true
                        break outerloop
                    }
                }
            }

            let autoMuteProcessInterval: null | number = null
            if (autoMuteEnabled) {
                audioContext.current = new AudioContext()
                log.verbose('automute', `automute enabled. starting interval.`)
                autoMuteProcessInterval = window.setInterval(processAutoMute, 50)
                // local audio stream volumes are updated at 10hz, so we'll process
                // auto-mute at 5hz for Nyquist reasons.
            }

            return () => {
                if (autoMuteProcessInterval) {
                    log.verbose('automute', `clearing automute interval`)
                    clearInterval(autoMuteProcessInterval)

                    audioContext.current?.close()
                    audioAnalyzers.current.clear()
                }
            }
        },
        [
            roomState.myAudioMuteState,
            roomState.myVideoMuteState,
            roomState.autoMuteEnabled,
            roomState.peers,
            state.mediaStreams.audioInputs,
            state.mediaStreams.video,
        ]
    )

    const parseRemoteChunk = useCallback(
        (senderSessionId: string, data_raw: any) => {
            try {
                // NOTE: if chunk starts with [ 26, 69, 223, 163 ] then it is the initial chunk.
                const isFirstChunk = data_raw[0] == 26 && data_raw[1] == 69 && data_raw[2] == 223 && data_raw[3] == 163
                // We check the last bits of the chunk to check to see if it is the "stopcode".
                const stopcode = 'muse-rec-stop'
                const stopcodeCandidate = String.fromCharCode.apply(null, data_raw.slice(stopcode.length * -1))
                const isStop = stopcodeCandidate === stopcode

                onRemoteRecordingChunkHandler({ senderSessionId, isFirstChunk, chunk: data_raw, isStop })
            } catch (e) {}
        },
        [onRemoteRecordingChunkHandler]
    )

    const universalDataReceivedHandler = useCallback(
        (peerRef: MusePeer, data_raw: any, isPayloadFromSfuRoom?: boolean) => {
            let obj
            try {
                log.silly(`universalDataReceivedHandler`, `called. parsing data_raw of length=${data_raw?.length}`)
                if (isPayloadFromSfuRoom) {
                    const decoder = new TextDecoder()
                    obj = JSON.parse(decoder.decode(data_raw))
                } else {
                    obj = JSON.parse(data_raw)
                }
            } catch (e) {
                parseRemoteChunk(peerRef.sessionId, data_raw)
                return
            }

            log.silly(`universalDataReceivedHandler`, `parsed into JSON successfully.`, { obj })
            const { type, data } = obj
            switch (type) {
                case 'request-manifest':
                    let manifest
                    if (roomStateRef.current.expectedNetworkTopology === 'p2p') {
                        manifest = generateP2pManifest(peerRef)
                    } else {
                        if (LiveKitManifestSupport.isSupported(peerRef.sessionId)) {
                            manifest = generateSfuManifest()
                        } else {
                            manifest = generateLegacySfuManifest(peerRef)
                        }
                    }

                    if (manifest) {
                        sendMessageToPeer(peerRef.sessionId, {
                            type: 'peer-manifest-broadcast',
                            data: manifest,
                        })
                    }
                    break
                case 'change-audio-volume':
                    const logtag = 'universalDataReceivedHandler:change-audio-volume'
                    let { deviceId, value } = data || {}
                    if (deviceId && value != null) {
                        log.verbose(
                            logtag,
                            `Received change-audio-volume request from user "${peerRef.username}" ` +
                                `on deviceId="${deviceId}".`
                        )
                        const matching = peerRef.localP2pAudios.find((a) => a.track.info.deviceId === deviceId) || null
                        if (matching == null) {
                            log.verbose(
                                logtag,
                                `no-op because the requested audio track ` +
                                    `with deviceId="${deviceId}" was not found.`
                            )
                            return
                        }

                        if (matching.gainNode == null) {
                            log.assert(logtag, `matching.gainNode expected to exist.`)
                            return
                        }
                        if (isNaN(value)) {
                            log.assert(
                                logtag,
                                `expected gainNode set value to be a number, instead got ${JSON.stringify(value)}.`
                            )
                            return
                        }
                        if (value < 0 || value > 10) {
                            value = 1
                        }

                        log.verbose(logtag, `setting 'gainNode.gain.value' := ${value}.`)
                        matching.gainNode.gain.value = value
                    }
                    break
                case 'start-remote-recording':
                    const sourceTrackId = (data || {}).sourceTrackId
                    onStartRemoteRecordingHandler(peerRef.sessionId, sourceTrackId)
                    break
                case 'stop-remote-recording':
                    onStopRemoteRecordingHandler()
                    break
                case 'remote-recording-error':
                    onStopRemoteRecordingHandler()
                    onRemoteRecordingErrorHandler(data || {})
                    break
                case 'midi':
                    log.silly('universalDataReceivedHandler/midi', `handling type="midi".`)

                    midiStatsRef.current.rxCount += 1

                    const midiDeviceId = obj.midiDeviceId || ''
                    log.silly('universalDataReceivedHandler/midi', `midiDeviceId="${midiDeviceId}".`)

                    // Get the matching device and handle the `isMuted` case.
                    const midiDevice =
                        peerRef.manifest.localP2pMidi.find((a) => a.sourceId === midiDeviceId) ||
                        peerRef.manifest.localSfuMidi.find((a) => a.sourceId === midiDeviceId) ||
                        null

                    log.isSilly() &&
                        log.silly(
                            'universalDataReceivedHandler/midi',
                            `get matching device and handle the 'isMuted' case:`,
                            {
                                'peerRef.manifest.localP2pMidi': peerRef.manifest.localP2pMidi,
                                'peerRef.manifest.localSfuMidi': peerRef.manifest.localSfuMidi,
                            }
                        )

                    const myMidiOutputDevice = currSelectedMidiOutputDevice.current || null

                    log.isSilly() &&
                        log.silly(
                            'universalDataReceivedHandler/midi',
                            `myMidiOutputDevice=(id="${myMidiOutputDevice?.id}" name="${myMidiOutputDevice?.name}").`
                        )

                    PeerMidiActivityEventEmitter.write(midiDeviceId, data)

                    if (peerRef.remoteMidiContolStates[midiDeviceId]?.isMuted) {
                        log.silly('universalDataReceivedHandler/midi', `returning because isMuted is true.`)
                        return
                    }

                    // We consider the case where `midiDevice` is undefined; if it is, then we do not check
                    // if it is muted and just play the note.
                    if (midiDevice == null) {
                        log.silly('universalDataReceivedHandler/midi', `no matching midi device; not playing.`)
                    } else if (midiDevice.isMuted) {
                        // console.log(`midi_reception: Looks like this device is muted. Not playing.`)
                        log.silly('universalDataReceivedHandler/midi', `output device is muted; not playing.`)
                    } else {
                        // If myMidiOutputDevice is selected, then we send the midi message there.
                        if (myMidiOutputDevice) {
                            log.silly(
                                'universalDataReceivedHandler/midi',
                                `this.myMidiOutputDevice is set, so sending data there.`
                            )
                            try {
                                myMidiOutputDevice.send([data[0], data[1], data[2]])
                            } catch (e) {
                                const msg =
                                    `this.myMidiOutputDevice.send(${JSON.stringify(data)})" ` +
                                    `threw an exception! ` +
                                    serializeError(e)
                                log.error('universalDataReceivedHandler/midi', msg)
                            }
                        } else {
                            log.silly(
                                'universalDataReceivedHandler/midi',
                                `this.myMidiOutputDevice is NOT set, so sending data to ElectronIPC.`
                            )
                            ElectronIPC.midiDataOut({ data: [data[0], data[1], data[2]] })
                        }
                    }
                    break
                case 'remote-host-control-mute-client':
                    myAudioDeviceMuteAll()
                    break
                case 'ping':
                    onReceivedPingFromPeer(peerRef.sessionId, data)
                    break
                case 'pong':
                    onReceivedPongFromPeer(peerRef.sessionId, data)
                    break
            }

            if (typeof type === 'string' && type.startsWith('rdp:')) {
                const rdpOpcode: string = type.split(':')[1]
                rdpHandler(peerRef.sessionId, rdpOpcode, data)
            }
        },
        []
    )

    // midi stuff
    const prevSelectedMidiInputDevice = useRef<MIDIInput | null>(null)
    useEffect(
        function attachMidiEventHandler() {
            const selectedDevice =
                state.mediaSources.midiInputs.find((a) => a.id === state.mediaSelection.midiInput) || null
            if (selectedDevice === prevSelectedMidiInputDevice.current) {
                return
            }

            const selectedDeviceId = selectedDevice?.id || ''

            const midimessageHandler = (ev: any) => {
                try {
                    const isMuted = roomStateRef.current.myMidiMuteState[selectedDeviceId]
                    if (isMuted) {
                        return
                    }

                    const { data } = ev

                    // filter out SysEx messages, ActiveSensing, and other undesired messages.
                    // Note: Its effect is not completely understood.
                    if ((data[0] & 0xf0) === 0xf0) {
                        return
                    }

                    const message = {
                        type: 'midi',
                        data: data,
                        midiDeviceId: selectedDeviceId,
                    }

                    midiStatsRef.current.txCount += 1

                    broadcastMessage(message)
                } catch (e) {}
            }

            if (prevSelectedMidiInputDevice.current) {
                try {
                    prevSelectedMidiInputDevice.current.removeEventListener('midimessage', midimessageHandler)
                } catch (e) {}
            }

            prevSelectedMidiInputDevice.current = selectedDevice

            if (selectedDevice) {
                selectedDevice.addEventListener('midimessage', midimessageHandler)
            }
        },
        [broadcastMessage, roomStateRef, state.mediaSelection.midiInput, state.mediaSources.midiInputs]
    )

    useEffect(
        function attachMidiOutputHandler() {
            currSelectedMidiOutputDevice.current =
                state.mediaSources.midiOutputs.find((a) => a.id === state.mediaSelection.midiOutput) || null
        },
        [state.mediaSelection.midiOutput, state.mediaSources.midiOutputs]
    )

    useEffect(
        function attachUniversalDataChannelHandler() {
            if (prevPeers == null) {
                return
            }

            // attach p2pConnection if changed.
            Object.keys(roomState.peers).forEach((sessionId) => {
                const peer = roomState.peers[sessionId]
                const now = roomState.peers[peer.sessionId]
                if (now == null) {
                    return
                }

                const prev = prevPeers[peer.sessionId] || {}

                // check if changed.
                if (now.p2pConnection && now.p2pConnection !== prev.p2pConnection) {
                    now.p2pConnection.on('data', (data_raw: any) => {
                        const peer = roomStateRef.current.peers[sessionId]
                        if (peer) {
                            universalDataReceivedHandler(peer, data_raw)
                        }
                    })
                }
            })
        },
        [prevPeers, roomState.peers, roomStateRef, universalDataReceivedHandler]
    )

    const sfuRoomPrev = useRef<Room | null>(null)
    useEffect(() => {
        if (roomState.sfuRoom && roomState.sfuRoom !== sfuRoomPrev.current) {
            sfuRoomPrev.current = roomState.sfuRoom

            roomState.sfuRoom.on(
                RoomEvent.DataReceived,
                (payload: Uint8Array, participant?: Participant, kind?: DataPacket_Kind) => {
                    const sessionId = participant?.identity || ''
                    const peer = roomStateRef.current.peers[sessionId]
                    if (peer) {
                        universalDataReceivedHandler(peer, payload, true)
                    }
                }
            )
        }
    }, [roomState.sfuRoom, roomStateRef, universalDataReceivedHandler])

    const roomHostUrn = roomState.host?.userUrn || null
    useEffect(() => {
        roomDispatch({
            type: 'update_room_context',
            value: { isHost: roomHostUrn === state.userUrn },
        })
    }, [roomHostUrn, state.userUrn, roomDispatch])

    // If we show the setup guide while leaving the session we get the
    // "Render frame was disposed before WebFrameMain could be accessed" error.
    useEffect(() => {
        const doShow = roomState.isExiting ? false : roomState.isHost && Object.keys(roomState.peers).length === 0
        if (doShow !== roomStateRef.current.isShowingGalleryQuickSetupGuide) {
            roomDispatch({
                type: 'update_room_context',
                value: { isShowingGalleryQuickSetupGuide: doShow },
            })
        }
    }, [roomState.isExiting, roomState.isHost, roomState.peers])

    const myAudioTracksDiff = useArrayDiff<AudioTrackWithInfo>(state.mediaStreams.audioInputs, (a, b) => a.id === b.id)
    useEffect(
        function setLoopbackHack() {
            for (let added of myAudioTracksDiff.added) {
                log.verbose('setLoopbackHack', 'Detected new audio track added: ', { info: added.info })
                log.verbose('setLoopbackHack', 'Got track: ', {
                    id: added.id,
                    label: added.info.label,
                    isSystemAudio: added.info.isSystemAudio,
                })

                // Set the internal audio loopback if is internal audio.
                if (added.info.enableLocalLoopback) {
                    if (added.info.isSystemAudio) {
                        log.verbose('setLoopbackHack', 'enableLocalAudioLoopback is set.')
                        if (added.info.audioEngineStreamUuid) {
                            getAudioEngineInstance().setAudioLoopbackStream(added)
                        }
                    } else {
                        log.verbose('setLoopbackHack', 'enableLocalAudioLoopback is set but isSystemAudio==false.')
                    }
                } else {
                    log.verbose('setLoopbackHack', 'enableLocalAudioLoopback is NOT set.')
                }
            }
        },
        [state.mediaStreams.audioInputs, myAudioTracksDiff.added]
    )

    const remoteDesktopBeingControlledStatusPrev = usePreviousV2(appState.appState.remoteDesktopBeingControlledStatus)
    useEffect(() => {
        if (
            remoteDesktopBeingControlledStatusPrev.current != null &&
            appState.appState.remoteDesktopBeingControlledStatus == null
        ) {
            const message = `${remoteDesktopBeingControlledStatusPrev.current.username} stopped controlling your desktop.`
            showSnackbar({
                severity: 'info',
                message: message,
            })
        }
    }, [remoteDesktopBeingControlledStatusPrev, appState.appState.remoteDesktopBeingControlledStatus, showSnackbar])

    // const remoteDesktopControlStatusPrev = usePreviousV2(roomState.remoteDesktopControlStatus)
    useEffect(() => {
        let message
        if (roomState.remoteDesktopControlStatus?.status === 'revoked') {
            message = `Desktop control revoked.`
        } else if (roomState.remoteDesktopControlStatus?.status === 'denied') {
            message = `Desktop control request denied.`
        } else if (roomState.remoteDesktopControlStatus?.status === 'allowed') {
            message = `You can now control ${roomState.remoteDesktopControlStatus.username}'s desktop.`
        }

        if (message) {
            showSnackbar({
                severity: 'info',
                message: message,
            })
        }
    }, [roomState.remoteDesktopControlStatus, showSnackbar])

    useEffect(
        function showRemoteRecordingStatusError() {
            if (roomState.remoteRecordingStatus.error) {
                // Show error snackbar
                let message
                switch (roomState.remoteRecordingStatus.error.code) {
                    case 'recordee_remote_recording_disabled':
                        message = 'Cannot record device. The user has disabled remote recording on their devices.'
                        break
                    case 'stream_timeout':
                        message =
                            'Remote recording error: the client unexpectedly stopped sending data or was too slow.'
                        break
                    case 'daw_recording_not_supported':
                        message = 'Device not supported, cannot record into the DAW.'
                        break
                    default:
                        message = `An error occurred while recording the remote device. code=${roomState.remoteRecordingStatus.error.code}`
                }

                showSnackbar({
                    severity: 'error',
                    message: message,
                    duration: 5000,
                })

                // Clear the recording button.
                endCurrRemoteAudioRecord()
            }
        },
        [roomState.remoteRecordingStatus.error]
    )

    useEffect(() => {
        if (!isDefinedDeviceId(state.mediaSelection.desktop)) {
            // revoke
            remoteDesktopControlRevoke()
        }
    }, [state.mediaSelection.desktop])

    const isControllingPeerDesktop = roomState.remoteDesktopControlStatus?.status
    useEffect(() => {
        const isEnabled = isControllingPeerDesktop === 'allowed'
        winKeyHook.setEnabled(isEnabled)
    }, [isControllingPeerDesktop])

    useEffect(function rtcStatsSetup() {
        const t = setInterval(async () => {
            if (roomStateRef.current && Object.keys(roomStateRef.current.peers).length > 0) {
                const hostPingMeasurement = getLatestHostPingMeasurement()

                // is set at `networkStatsEvent` handler in useAudioEngineService.
                const audioEngineNetworkStats = (window as any).audioEngineNetworkStats as AudioEngineNetworkStats

                const perStreamRemoteStatsSummary: { [key: string]: AudioEngineStreamRemoteStatsSummary | null } = {}
                Object.keys(roomStateRef.current.peers).forEach((sessionId) => {
                    const peer = roomStateRef.current.peers[sessionId]
                    peer.remoteAudios.forEach((a) => {
                        if (a.track instanceof FakeMediaStreamTrack) {
                            perStreamRemoteStatsSummary[a.track.id] = a.track.remoteStatsSummary.currSummary
                        }
                    })
                })

                RtcStatsUtils.setStats(
                    roomStateRef.current,
                    hostPingMeasurement,
                    audioEngineNetworkStats,
                    perStreamRemoteStatsSummary,
                    midiStatsRef.current
                )
            }
        }, 5_000)

        return () => {
            clearInterval(t)
        }
    }, [])

    useEffect(() => {
        persistent.delete((k) => k.startsWith('peerAudioVolumeCacheByDeviceId'))

        if (env.TEST_OS_AUDIO_SYSTEM_RESET) {
            setInterval(() => {
                getAudioEngineInstance().testCrash()

                userStateRef.current.mediaStreams.audioInputs.forEach((track) => {
                    if (typeof track?.stop === 'function' && typeof track?.dispatchEvent === 'function') {
                        track.dispatchEvent(new Event('ended'))
                    }
                })
            }, 15000)
        }

        if (env.TEST_NEW_AUDIO_SOURCES_DETECTED) {
            setTimeout(() => {
                let fakeAudioSource1: AudioSourceInfo = {
                    deviceId: '__test__',
                    groupId: '__test__',
                    kind: 'audioinput',
                    label: 'Test audio input device',
                    nativeDeviceId: null,
                    audioEngineDeviceId: null,
                    deviceUid: '0',
                    displayLabel: 'Test audio input device',
                    defaultSampleRate: 0,
                    maxInputChannels: 2,
                    maxOutputChannels: 0,
                    hostApiName: '',
                    isSystemAudio: false,
                    isMuseAudioShare: false,
                    isMuseDAWBridge: false,
                }
                let fakeAudioSource2: AudioSourceInfo = {
                    deviceId: '__test__',
                    groupId: '__test__',
                    kind: 'audioinput',
                    label: 'Test audio input device',
                    nativeDeviceId: null,
                    audioEngineDeviceId: null,
                    deviceUid: '0',
                    displayLabel: 'Test audio input device',
                    defaultSampleRate: 0,
                    maxInputChannels: 2,
                    maxOutputChannels: 0,
                    hostApiName: '',
                    isSystemAudio: false,
                    isMuseAudioShare: false,
                    isMuseDAWBridge: false,
                }

                dispatch({
                    type: 'update_media_sources',
                    value: {
                        audioInputs: [
                            ...userStateRef.current.mediaSources.audioInputs,
                            fakeAudioSource1,
                            fakeAudioSource2,
                        ],
                        updatedByEventCt: userStateRef.current.mediaSources.updatedByEventCt + 1,
                    },
                })
            }, 10000)
        }
    }, [])

    useEffect(
        function detectAndHandleRemoteAudioEngineAudio() {
            ;(async () => {
                const dispatchList: { sessionId: string; peer: Partial<MusePeer> }[] = []

                const room: IAudioEngineRoom | null = roomStateRef.current.audioEngineRoom

                const peers = Object.keys(roomState.peers).map((sessionId) => roomState.peers[sessionId])
                for (let peer of peers) {
                    if (!peer.manifest) {
                        log.verbose(
                            'detectAndHandleRemoteAudioEngineAudio',
                            'warning: no manifest for peer; skipping.',
                            {
                                sessionId: peer.sessionId,
                                username: peer.username,
                            }
                        )
                        continue
                    }

                    const peerManifestAudios: (P2pLocalAudioManifest | SfuLocalAudioManifest)[] = [
                        ...peer.manifest.localP2pAudios,
                        ...peer.manifest.localSfuAudios,
                    ]

                    // see if there is an AudioEngine localP2pAudio track that is not
                    // present in `roomStateRef.current.peers[sessionId].remoteAudios`.
                    const remoteAudiosToAdd: AudioEngineRemoteAudio[] = []
                    const remoteAudios = peer.remoteAudios
                    for (let i = 0; i < peerManifestAudios.length; ++i) {
                        const a = peerManifestAudios[i]
                        // if AudioEngine stream:
                        if (a.info.audioEngineStreamUuid) {
                            const match = remoteAudios.find((b) => a.info.audioEngineStreamUuid === b.track.id)
                            if (!match) {
                                log.info('audioEngineReceiveRemoteStream', 'calling room.receiveRemoteStream().', {
                                    streamUuid: a.info.audioEngineStreamUuid,
                                    info: a.info,
                                })
                                if (room) {
                                    const remoteAudio: AudioEngineRemoteAudio | AudioEngineError =
                                        await room.receiveRemoteStream(a.info.audioEngineStreamUuid, a.info)
                                    if (remoteAudio instanceof AudioEngineError) {
                                        log.error(
                                            'audioEngineReceiveRemoteStream',
                                            'room.recieveRemoteStream() returned error',
                                            {
                                                info: a.info,
                                                error: remoteAudio,
                                            }
                                        )
                                    } else {
                                        log.info(
                                            'audioEngineReceiveRemoteStream',
                                            'room.receiveRemoteStream() returned successfully.',
                                            {
                                                streamUuid: remoteAudio.info.audioEngineStreamUuid,
                                                info: remoteAudio.info,
                                            }
                                        )
                                        // set volume from cached
                                        remoteAudio.info.channelSelection.forEach((chgrp) => {
                                            const v = getCachedRemoteAudioVolume(peer, remoteAudio, chgrp)
                                            remoteAudio.track.setVolumeOfChannelGroup(v, chgrp)
                                        })

                                        remoteAudiosToAdd.push(remoteAudio)
                                    }
                                } else {
                                    // temporary: room will be null, esp on the browser session. Make a dummy stream.
                                    const r: AudioEngineRemoteAudio = {
                                        fromTopology: 'ae',
                                        track: new FakeMediaStreamTrack({
                                            id: a.info.audioEngineStreamUuid,
                                            label: a.info.displayLabel,
                                            channelSelection: a.info.channelSelection,
                                            channelUnpackingMap: a.info.channelUnpackingMap,
                                        }),
                                        info: a.info,
                                    }
                                    remoteAudiosToAdd.push(r)
                                }
                            }
                        }
                    }

                    // handle the case when the remote device is removed
                    const remoteAudiosToRemove = new Set()
                    remoteAudios
                        .filter((a) => a.info.audioEngineStreamUuid)
                        .forEach((a) => {
                            const match = peerManifestAudios.find((b) => a.track.id === b.info.audioEngineStreamUuid)
                            if (!match) {
                                remoteAudiosToRemove.add(a.track.id)
                                roomStateRef.current.audioEngineRoom?.removeRemoteStream(a.track.id)
                            }
                        })

                    // create `dispatchList`
                    if (remoteAudiosToAdd.length > 0 || remoteAudiosToRemove.size > 0) {
                        dispatchList.push({
                            sessionId: peer.sessionId,
                            peer: {
                                remoteAudios: [...remoteAudios, ...remoteAudiosToAdd].filter(
                                    (a) => !remoteAudiosToRemove.has(a.track.id)
                                ),
                            },
                        })
                    }
                }

                // find removed peers and remove audio engine audio stream
                if (prevPeers) {
                    Object.keys(prevPeers).forEach((sessionId) => {
                        if (!roomState.peers[sessionId]) {
                            const peer = prevPeers[sessionId]
                            log.verbose(
                                'detectAndHandleRemoteAudioEngineAudio',
                                `detected ` + `removed peer sessionId="${sessionId}" username="${peer.username}".`
                            )
                            const streamUuids = peer.remoteAudios.map((a) => a.info.audioEngineStreamUuid)
                            for (let streamUuid of streamUuids) {
                                if (streamUuid) {
                                    log.verbose(
                                        'detectAndHandleRemoteAudioEngineAudio',
                                        `calling ` + `removeRemoteStream() for streamUuid="${streamUuid}."`
                                    )
                                    roomStateRef.current.audioEngineRoom?.removeRemoteStream(streamUuid)
                                }
                            }

                            if (
                                roomStateRef.current.lastScreenControlRequestResponse?.requesterSessionId === sessionId
                            ) {
                                // also revoke screen control
                                remoteDesktopControlRevoke()
                            }
                        }
                    })
                }

                // dispatch
                if (dispatchList.length > 0) {
                    roomDispatch({
                        type: 'update_peer',
                        value: dispatchList,
                    })
                }
            })()
        },
        [roomState.peers]
    )

    useEffect(() => {
        log.checkpoint('remote_desktop_being_controlled_status_updated', '', {
            status: appState.appState.remoteDesktopBeingControlledStatus,
        })
    }, [appState.appState.remoteDesktopBeingControlledStatus])

    useEffect(() => {
        log.checkpoint('remote_desktop_control_status_updated', '', {
            status: roomState.remoteDesktopControlStatus,
        })
    }, [roomState.remoteDesktopControlStatus])

    useEffect(() => {
        // @ts-ignore
        window.roomState = roomState
    }, [roomState])

    useEffect(() => {
        // @ts-ignore
        window.userState = state
    }, [state])

    useEffect(() => {
        // @ts-ignore
        window.appState = appState
    }, [appState])

    return (
        <>
            {roomState.expectedNetworkTopology === 'p2p' &&
                Object.keys(roomState.peers).map((peerSessionId) => {
                    const peer = roomState.peers[peerSessionId] || null
                    if (peer) {
                        const key = `${peerSessionId}_${peer.appSerialId}_${peer.userUrn}`
                        // connection closes if this component unloads so be careful with its state management.
                        return <MuseP2pPeerManager key={key} peer={peer} />
                    } else {
                        return <></>
                    }
                })}
        </>
    )
}
